import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = unknown, false = anonymous, object = logged in
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 on first load is expected (anonymous); log others.
      if (err?.response?.status !== 401) {
        console.error("AuthContext fetchMe failed:", err);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const loginWithGoogle = useCallback(async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google", { session_id: sessionId });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // If we're an operator, mark ourselves offline server-side BEFORE the
      // auth cookie is dropped. Without this, closing the session with the
      // toggle in "Disponible" leaves the dispatcher panel showing us as
      // LIBRE forever and trips can still be routed to us.
      if (user && user.role === "operator") {
        try {
          await api.post("/driver/status", { driver_status: "offline" });
        } catch (statusErr) {
          console.debug("driver_status offline on logout skipped:", statusErr?.message || statusErr);
        }
      }
      await api.post("/auth/logout");
    } catch (err) {
      console.error("AuthContext logout failed:", err);
    }
    setUser(false);
  }, [user]);

  const value = useMemo(
    () => ({ user, loading, login, loginWithGoogle, logout, refresh: fetchMe }),
    [user, loading, login, loginWithGoogle, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
